import { calculateAge, parseToDate, toIsoDate } from "../../utils/dateUtils";
import type { ChildSummary, Classroom, EmailAssessment, EntityId, HttpErrorLike, SchoolYear, Teacher, TeacherAssignment } from "./types";

export const isHttpErrorLike = (error: unknown): error is HttpErrorLike =>
  typeof error === "object" && error !== null && "response" in error;

export const httpStatus = (error: unknown): number | undefined =>
  isHttpErrorLike(error) ? error.response?.status : undefined;

export const backendMessage = (error: unknown): string | undefined =>
  isHttpErrorLike(error) ? error.response?.data?.message : undefined;

export const idEquals = (left: EntityId | null | undefined, right: EntityId | null | undefined) =>
  left != null && right != null && String(left) === String(right);

export const entityKey = (id: EntityId | null | undefined) => (id == null ? "" : String(id));

export const teacherName = (teacher: Teacher | TeacherAssignment | null | undefined) =>
  [
    teacher?.firstName,
    teacher?.lastName,
  ]
    .filter(Boolean)
    .join(" ")
    .trim() ||
  ("teacherName" in (teacher ?? {}) ? String((teacher as TeacherAssignment).teacherName ?? "") : "") ||
  ("fullName" in (teacher ?? {}) ? String((teacher as TeacherAssignment).fullName ?? "") : "");

export const teacherInitials = (teacher: Teacher | null | undefined) =>
  [teacher?.firstName, teacher?.lastName]
    .filter(Boolean)
    .map((part) => String(part).trim().charAt(0).toUpperCase())
    .join("")
    .slice(0, 2) || "?";

export const classroomChildren = (classroom: Classroom) =>
  (Array.isArray(classroom.children) ? classroom.children : Array.isArray(classroom.currentChildren) ? classroom.currentChildren : []).filter(
    (child): child is ChildSummary => Boolean(child && !child.withdrawnAt && !child.deceasedAt)
  );

export const schoolYearLabel = (year: SchoolYear | null | undefined) => {
  if (!year) return "";
  if (year.label) return year.label;
  if (year.name) return year.name;
  const startYear = parseToDate(year.startDate)?.getFullYear();
  const endYear = parseToDate(year.endDate)?.getFullYear();
  return startYear && endYear ? `${startYear} - ${endYear}` : "";
};

export const classroomYearLabel = (classroom: Classroom) => {
  const label = schoolYearLabel(classroom.schoolYear);
  if (label) return label;
  const startYear = parseToDate(classroom.startDate)?.getFullYear();
  const endYear = parseToDate(classroom.endDate)?.getFullYear();
  return startYear && endYear ? `${startYear} - ${endYear}` : "";
};

export const toDateInput = (value: SchoolYear["startDate"] | string | null | undefined) => toIsoDate(value) || "";

export const getYearNumber = (value: SchoolYear["startDate"] | string | null | undefined) =>
  parseToDate(value)?.getFullYear() ?? null;

export const getTime = (value: SchoolYear["startDate"] | string | null | undefined) =>
  parseToDate(value)?.getTime() ?? 0;

/**
 * School years like the school year dropdown: the current one (today between start and end), then the coming ones
 * (soonest first), then the old ones (most recent first).
 */
export const groupSchoolYears = <T extends Pick<SchoolYear, "startDate" | "endDate">>(years: T[], today: Date = new Date()) => {
  const day = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
  const startOf = (year: T) => parseToDate(year.startDate);
  const endOf = (year: T) => parseToDate(year.endDate);
  const current: T[] = [];
  const upcoming: T[] = [];
  const past: T[] = [];
  years.forEach((year) => {
    const start = startOf(year)?.getTime();
    const end = endOf(year)?.getTime();
    if (start != null && start > day) upcoming.push(year);
    else if (start != null && end != null && start <= day && day <= end) current.push(year);
    else past.push(year);
  });
  const byStart = (a: T, b: T) => getTime(a.startDate) - getTime(b.startDate);
  return {
    current: current.sort(byStart),
    upcoming: upcoming.sort(byStart),
    past: past.sort((a, b) => byStart(b, a)),
  };
};

export const computeGenderStats = (children: ChildSummary[]) => {
  if (children.length === 0) return null;
  const counts = children.reduce(
    (acc, child) => {
      if (child.gender === "M") acc.male += 1;
      else if (child.gender === "F") acc.female += 1;
      else acc.other += 1;
      return acc;
    },
    { male: 0, female: 0, other: 0 }
  );
  const total = counts.male + counts.female + counts.other;
  return total > 0 ? { counts, total } : null;
};

export const computeKindergartenAgeDistribution = (classroom: Classroom, children: ChildSummary[]) => {
  if (classroom.classType !== "KINDERGARTEN" || children.length === 0) return null;
  const startDate = parseToDate(classroom.startDate);
  const reference = startDate ? new Date(startDate.getFullYear(), 11, 31) : new Date();
  const counts = { young: 0, middle: 0, old: 0 };

  children.forEach((child) => {
    const age = calculateAge(child.dateBirth, reference);
    if (!Number.isFinite(age)) return;
    if (age <= 4) counts.young += 1;
    else if (age === 5) counts.middle += 1;
    else if (age >= 6) counts.old += 1;
  });

  const total = counts.young + counts.middle + counts.old;
  return total > 0 ? { counts, total } : null;
};

export const backendEmailAssessment = (teacher: Teacher) => {
  const value =
    teacher.emailValidation ||
    teacher.emailAssessment ||
    teacher.emailCheck ||
    teacher.email_validation ||
    teacher.email_assessment ||
    teacher.email_check ||
    null;
  if (!value) return null;

  const statusRaw = value.status ?? value.kind ?? value.level ?? null;
  const status = statusRaw != null ? String(statusRaw).trim().toLowerCase() : "";
  const normalized =
    status === "valid" || status === "ok"
      ? "valid"
      : status === "invalid" || status === "error"
      ? "invalid"
      : status === "suspicious" || status === "warning" || status === "warn"
      ? "suspicious"
      : null;
  if (!normalized) return null;

  const reasons = Array.isArray(value.reasons) ? value.reasons.map(String).filter(Boolean) : [];
  const suggestion = value.suggestedEmail != null ? String(value.suggestedEmail) : value.suggestion != null ? String(value.suggestion) : null;
  const source = value.source != null ? String(value.source) : "backend";
  return { status: normalized, reasons, suggestion, source } satisfies EmailAssessment & { status: string };
};

export const isLikelyValidEmailFormat = (email: string | null | undefined) => {
  const raw = email == null ? "" : String(email).trim();
  if (!raw) return true;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(raw);
};
